/* ================================================================ */
// MAIN LAYOUT CONTAINER | MainLayout.jsx
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import React, {Suspense, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useReactRouter from 'use-react-router';
// MaterialUI
import Skeleton from '@material-ui/lab/Skeleton';
import Typography from '@material-ui/core/Typography';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import makeStyles from '@material-ui/styles/makeStyles';
// Constants
import {LayoutTypes, AlignmentTypes, NavigationTypes, MenuTypes} from '@myglobe/core/dist/types';
// Validators
import LayoutConfig from './LayoutConfig';
// Component Resolver
import resolveComponents from './ComponentResolver';
// MyGlobe Library
// Hooks
import useMedia from '@myglobe/core/dist/hooks/useMedia';
import useLocalStorage from '../../utils/useLocalStorage';
// > ASYNC
//const Drawer = React.lazy(()=>import('@myglobe/core/dist/Layout/Drawer'));
const GridLayout = React.lazy(()=>import('@myglobe/core/dist/Layout/Grid'));
const LayeredLayout = React.lazy(()=>import('@myglobe/core/dist/Layout/Layered'));
/* ========================================== */
// Style Settings
/* ========================================== */
const useStyles = makeStyles(({
    'body': {
        transition: 'all 0.25s linear'
    },
    root: {
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        height: '100vh',
        display: 'flex',
        transition: 'all 500ms cubic-bezier(0.215, 0.610, 0.355, 1.000)',
    },
    error: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
    }
}));

/* ========================================== */
/**
 * Main Layout
 * -----------
 * Main application layout
 *
 * @param {object} props component props
 */
const MainLayout = React.memo((props) => {
    const classes = useStyles();
    const {title, config, isLoading, isError, error} = props;
    const {desktop, mobile} = config;
    const [loadedComponents, setComponents] = useState([]);
    //(min-width: 960px)
    const schema = useMedia(['(min-width: 740px)', '(min-width: 0px)'], [desktop, mobile], desktop);
    
    const [disabledIntro, setDisableIntroduction] = useLocalStorage('introduction-disabled');
    
    // maybe needs to be moved into core?
    const {history} = useReactRouter();
    

    useEffect(() => {
        var listenerDisconnect = history.listen(historyListener);
        return () => {
            listenerDisconnect();
        }
    },[])

    function historyListener (location, action) {
        // location is an object like window.location
        gtagUAs.forEach(x=>{
            console.log('config', x, {'page_path': location.pathname});
            window.gtag('config', x, {'page_path': location.pathname});            
        });
    };

    // Winter check
    if(window.navigator.userAgent.toLowerCase().indexOf('edge')>-1) {
        return <div className={classes.error}>
                <Typography variant="h1" color="secondary">Error</Typography>
                <Typography variant="body1" color="secondary">Please upgrade your Edge to the latest which runs on chromium.</Typography>
            </div>
    }



    useEffect(()=>{
        console.debug(`${MainLayout.displayName} | `, '(update) handleSchema -', 'schema:', schema);
        const components = schema && resolveComponents(schema.components);
        console.log(components);
        components && setComponents(components);
    }, [schema]);

    const rootClasses = classNames(classes.root);
    return (
        <main className={rootClasses}>
            <Suspense fallback={<Skeleton variant="rect" width={210} height={118} />}>
                {isError ?
                    <div className={classes.error}>
                        <Typography variant="h1" color="secondary">Unable to load globe {title}</Typography>
                        <Typography variant="body1" color="secondary">Error: {error.message}</Typography>
                    </div>
                    : (
                        schema.type === LayoutTypes.GRID ? (
                            <GridLayout>
                                {loadedComponents}
                                {/*<Sidebar
                                    logo={logo}
                                    navigation={navigation}
                                    xs={{collapsed: 2, expanded:6}}
                                />
                                <Page />
                                <MapContainer
                                    xs={{collapsed: 10, expanded:6}}
                                />
                                {//<Login />
                                */}
                            </GridLayout>
                        ) : (
                            <LayeredLayout>
                                {/*}
                                <Introduction key="introduction" isOpen={!disabledIntro} />
                                */}
                                {loadedComponents}
                            </LayeredLayout>
                        )
                    )}
            </Suspense>
        </main>
    );
});
/* ========================================== */
// Props
/* ========================================== */
MainLayout.propTypes = {
    config: PropTypes.shape({
        logo: PropTypes.string,
        mobile: LayoutConfig,
        desktop: LayoutConfig,
        navigation: PropTypes.shape({
            type: PropTypes.oneOf(Object.values(NavigationTypes)),
            align: PropTypes.oneOf(Object.values(AlignmentTypes)),
            props: PropTypes.object,
            menu: PropTypes.shape({
                type: PropTypes.oneOf(Object.values(MenuTypes)),
                items: PropTypes.arrayOf(PropTypes.shape({
                    title: PropTypes.string.isRequired,
                    path: PropTypes.string.isRequired,
                    position: PropTypes.number,
                    items: PropTypes.arrayOf(PropTypes.shape({
                        title: PropTypes.string.isRequired,
                        path: PropTypes.string.isRequired,
                        position: PropTypes.number,
                    }))
                }))
            })
        })
    }),
    isLoading: PropTypes.bool.isRequired,
    theme: PropTypes.object
};
/*
MainLayout.defaultProps = {
    config: {
        logo: '/assets/logo.png',
        /*mobile: {
            type: LayoutTypes.LAYERED,
            align: AlignmentTypes.BOTTOM
        },
        desktop: {
            type: LayoutTypes.GRID,
            align: AlignmentTypes.LEFT
        },
        navigation: {
            type: NavigationTypes.INLINE,
            align: AlignmentTypes.TOP,
            menu: []
        }
    }
};*/
MainLayout.types = LayoutTypes;
MainLayout.displayName = 'MainLayout';
MainLayout.whyDidYouRender = true;

const Introduction = ({isOpen}) => {
    return (
        <Dialog open={isOpen}>
            <DialogContent>
                <DialogTitle>
                    <Typography variant="h2">Securing Water Supply for our Region</Typography>
                </DialogTitle>
                <DialogContentText>
                    Welcome to the Future Water Project 2060 visualisation tool. The purpose of this visualisation tool is 
                    to provide the user with a snapshot of FWP 2060’s main key elements and to gain a better 
                    understanding of some of the major challenges in securing our region’s future water supply needs.
                </DialogContentText>
                <Typography variant="h3">HOW TO USE THE FWP2060 VISUAL TOOL</Typography>
                <Typography variant="body1"></Typography>
            </DialogContent>
        </Dialog>
    );
};

/* ========================================== */
// Exports
/* ========================================== */
export default MainLayout;